import React, { useState, useLayoutEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Grid,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import projectService from '../services/projectService';
import useNotification from '../hooks/useNotification';
import { RichTextEditor, PageTitleContainer } from '../components/common';

const CreateChildProject = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams();
  const { showNotification } = useNotification();

  // Detect type from URL path
  const isChangeRequest = location.pathname.includes('/change-request');
  const type = isChangeRequest ? 'change-request' : 'new-feature';

  // Dynamic content based on type
  const content = {
    'new-feature': {
      title: 'Create New Feature',
      titleLabel: 'Title',
      titlePlaceholder: 'Enter feature title',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Enter feature description',
      infoMessage: 'This feature will be created as a child of the current project. You will then start a conversation to define the feature details.',
      successMessage: 'Feature created successfully',
      errorMessage: 'Failed to create feature',
      taskId: 'feature-info',
      buttonText: 'Create & Start Conversation'
    },
    'change-request': {
      title: 'Create Change Request',
      titleLabel: 'Title',
      titlePlaceholder: 'Enter change request title',
      descriptionLabel: 'Description',
      descriptionPlaceholder: 'Describe the changes you want to request',
      infoMessage: 'This change request will be created as a child of the current project. You will then start a conversation to define the change details.',
      successMessage: 'Change request created successfully',
      errorMessage: 'Failed to create change request',
      taskId: 'change-request-info',
      buttonText: 'Create & Start Conversation'
    }
  };

  const currentContent = content[type];

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Refs for height calculation
  const contentRef = useRef(null);

  // State for content height
  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Page title header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name || formData.name.trim() === '') {
      newErrors.name = 'Title is required';
    }

    if (!formData.description || formData.description.trim() === '') {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProceed = async () => {
    if (!validate()) {
      showNotification('Please fill in all required fields', 'error');
      return;
    }

    setLoading(true);

    try {
      // Create the new child project (feature or change request)
      const childProjectData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        type: type,
        parentId: projectId,
        parentType: 'project'
      };

      const response = await projectService.createProject(childProjectData);
      console.log('response', response);

      showNotification(currentContent.successMessage, 'success');

      // Redirect to conversation page with appropriate task and fromCreate flag
      navigate(`/projects/${response.id}/conversation?task=${currentContent.taskId}&fromCreate=true`);

    } catch (error) {
      console.error(`Error creating ${type}:`, error);

      // Handle 422 validation errors from backend
      // Note: api.js interceptor transforms error to have .status and .data instead of .response
      if (error.status === 422 && error.data?.detail) {
        const validationErrors = error.data.detail;
        if (Array.isArray(validationErrors)) {
          const newErrors = {};
          validationErrors.forEach(err => {
            const field = err.loc[err.loc.length - 1]; // Get field name (e.g., 'name', 'description')
            newErrors[field] = err.msg;
          });
          setErrors(newErrors);
          showNotification('Please fix the validation errors', 'error');
        } else {
          showNotification('Validation failed. Please check your input.', 'error');
        }
      } else {
        showNotification(error.message || currentContent.errorMessage, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate(`/projects/${projectId}`);
  };

  return (
    <Box
      sx={{
        width: '100%',
        bgcolor:'#f5f6f7',
        minHeight: '100vh',
        pb: 4,
        px: 6,
        pt: 4,
        overflowX: 'hidden',
      }}
    >
      {/* Header */}
      <PageTitleContainer
        title={currentContent.title}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px'
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      {/* Main Content */}
      <Box sx={{ maxWidth: 1200, mx: 'auto', pt: 0, width: '100%' }}>
        <Paper
          elevation={0}
          ref={contentRef}
          sx={{
            borderRadius: '4px',
            p: { xs: 2, sm: 3, md: 4, lg: '20px' },
            width: '100%',
            height: contentHeight,
            bgcolor: 'white',
            overflowX: 'hidden',
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
            backgroundImage: 'none',
          }}
        >
          {/* Form */}
          <Box sx={{ overflow: 'hidden' }}>
            <Grid container spacing={2} sx={{ mb: 2 }}>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                  {currentContent.titleLabel} <span style={{ color: '#F89939' }}>*</span>
                </Typography>
                <TextField
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder={currentContent.titlePlaceholder}
                  fullWidth
                  error={!!errors.name}
                  helperText={errors.name}
                  disabled={loading}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      color: 'var(--color-blue, #475969)',
                      height: '45px',
                      transition: 'all 0.2s',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#475969',
                        borderWidth: '1px',
                      },
                      '&.Mui-error .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#f44336',
                      }
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#4759694d',
                      border: '1px solid #4759694d',
                      borderRadius: '4px',
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: '14px',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Typography variant="body1" sx={{ mb: 1, fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }}>
                {currentContent.descriptionLabel} <span style={{ color: '#F89939' }}>*</span>
              </Typography>
              <Box sx={{
                border: '1px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '8px',
                overflow: 'hidden',
                maxWidth: '100%',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
                transition: 'all 0.2s'
              }}>
                <RichTextEditor
                  value={formData.description}
                  onChange={(value) => {
                    setFormData(prev => ({ ...prev, description: value }));
                    if (errors.description) {
                      setErrors(prev => ({ ...prev, description: '' }));
                    }
                  }}
                  placeholder={currentContent.descriptionPlaceholder}
                  disabled={loading}
                  minHeight="150px"
                  sx={{
                    '& .ck-editor__editable': {
                      minHeight: '150px',
                      p: 2,
                      fontFamily: 'inherit',
                      fontSize: '14px',
                      color: 'var(--color-blue, #475969)',
                      '& .ck-placeholder': {
                        color: '#a2abb3',
                        opacity: 0.7,
                        fontSize: '14px'
                      }
                    },
                    '& .ck-toolbar': {
                      border: 'none',
                      borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                      backgroundColor: 'rgba(0, 0, 0, 0.02)'
                    }
                  }}
                />
              </Box>
              {errors.description && (
                <Typography variant="caption" sx={{ color: '#f44336', marginLeft: '4px', fontSize: '12px', mt: 0.5, display: 'block' }}>
                  {errors.description}
                </Typography>
              )}
            </Box>

            {/* Info Alert */}
            <Alert severity="info" sx={{ borderRadius: 1, mb: 3 }}>
              {currentContent.infoMessage}
            </Alert>

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, pt: 2, pb: 1 }}>
              <Button
                variant="contained"
                onClick={handleProceed}
                disabled={loading}
                sx={{
                  bgcolor: '#485869',
                  color: 'white',
                  '&:hover': {
                    bgcolor: '#3a4a59',
                  },
                  borderRadius: '5px',
                  px: 5,
                  py: 0.5,
                  minWidth: '120px',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                }}
              >
                {loading ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CircularProgress size={16} sx={{ color: 'white', mr: 1 }} />
                    Creating...
                  </Box>
                ) : (
                  'Save'
                )}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default CreateChildProject;
